import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { QueryClient, QueryObserver } from '@tanstack/react-query';
import type { QueryObserverResult } from '@tanstack/react-query';
import { normalizeSkillProjection } from './contracts';
import type { SkillAPIPort, SkillAvailability, SkillProjection, SkillReadOwner } from './contracts';

const QUERY_STATUS: Record<string, SkillAvailability['status']> = {
  pending: 'pending', success: 'ready', error: 'failed',
};
const abortError = () => Object.assign(new Error('Skill scope is no longer active'), { name: 'AbortError' });
const presentResult = (result: QueryObserverResult<SkillProjection, Error>, available: boolean): SkillAvailability => ({
  status: available ? QUERY_STATUS[result.status] : 'unavailable', projection: result.data ?? null, error: result.error,
});

/** The library owns IO, coalescing, cancellation and accepted cache; this hook binds instance scope. */
export const useSkills = ({ skillAPI, instanceId = '', userId = '', enabled = true, settlementKey = '' }: {
  skillAPI?: SkillAPIPort; instanceId?: string; userId?: string; enabled?: boolean; settlementKey?: string;
} = {}) => {
  const currentOwner = useRef<SkillReadOwner | null>(null);
  const binding = useMemo(() => {
    const client = new QueryClient();
    const queryKey = ['skill-activation', instanceId, userId];
    const available = Boolean(skillAPI?.getSkills && instanceId);
    const lifecycle = { active: false, enabled: false };
    const queryFn = async ({ signal }: { signal: AbortSignal }) => {
      if (!skillAPI) throw new TypeError('Skill reader unavailable');
      const projection = normalizeSkillProjection(await skillAPI.getSkills({ instanceId, signal }), instanceId);
      const accepted = client.getQueryData<SkillProjection>(queryKey);
      if (accepted && projection.scope.tenant_id !== accepted.scope.tenant_id) throw new TypeError('Changed Skill activation scope');
      if (accepted && projection.revision < accepted.revision) throw new TypeError('Stale Skill activation revision');
      return projection;
    };
    const observer = new QueryObserver<SkillProjection, Error>(client, {
      queryKey, queryFn, enabled: false, retry: false, staleTime: Infinity,
    });
    const owner: SkillReadOwner = {
      isCurrent: () => lifecycle.active && lifecycle.enabled && available && currentOwner.current === owner,
      getSnapshot: () => presentResult(observer.getCurrentResult(), available && lifecycle.enabled),
      readFresh: async () => {
        if (!owner.isCurrent()) throw abortError();
        const projection = await client.fetchQuery({ queryKey, queryFn, retry: false, staleTime: 0 });
        if (!owner.isCurrent()) throw abortError();
        return projection;
      },
    };
    return { client, observer, owner, lifecycle, queryKey, available };
  }, [instanceId, skillAPI, userId]);
  currentOwner.current = binding.owner;
  binding.lifecycle.enabled = enabled;
  const subscribe = useCallback((notify: () => void) => binding.observer.subscribe(notify), [binding]);
  const getSnapshot = useCallback(() => binding.observer.getCurrentResult(), [binding]);
  const result = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    binding.lifecycle.active = true;
    return () => {
      binding.lifecycle.active = false;
      void binding.client.cancelQueries({ queryKey: binding.queryKey });
    };
  }, [binding]);
  useEffect(() => {
    if (!binding.owner.isCurrent()) return;
    void binding.owner.readFresh().catch(() => {});
    return () => { void binding.client.cancelQueries({ queryKey: binding.queryKey }); };
  }, [binding, enabled, settlementKey]);
  return useMemo(() => ({ ...presentResult(result, binding.available && enabled), owner: binding.owner }), [binding, enabled, result]);
};
